/**
 * Modal UI Component
 * Pure vanilla JS/TS implementation for consent and settings modals
 */

import type { ConsentManager } from "./consent-manager";
import type { PluginConfig } from "./config";

export class Modal {
	private manager: ConsentManager;
	private config: PluginConfig;
	private consentModal: HTMLElement | null = null;
	private settingsModal: HTMLElement | null = null;
	private overlay: HTMLElement | null = null;
	private focusTrap: FocusTrap | null = null;

	constructor(manager: ConsentManager, config: PluginConfig) {
		this.manager = manager;
		this.config = config;
	}

	private isInlineLayout(layout: string): boolean {
		return layout === "inline-top";
	}

	private needsOverlay(layout: string): boolean {
		// Centered modals and drawers need an overlay for proper UX
		// Floating and banner layouts don't need one
		return layout === "centered-modal" || layout.startsWith("drawer-");
	}

	public show(): void {
		const wasJustCreated = !this.consentModal;
		if (!this.consentModal) {
			this.createConsentModal();
		}

		const layout = this.config.appearance.consentLayout;
		const inline = this.isInlineLayout(layout);

		// Show overlay if consent is required OR if the layout needs it
		if (!inline && (this.config.consentRequired || this.needsOverlay(layout))) {
			this.showOverlay();
		}

		// If just created, force reflow before adding visible class to trigger enter animation
		if (wasJustCreated) {
			void this.consentModal!.offsetHeight;
		}

		this.consentModal!.classList.add("dcbl-cc-modal--visible");
		this.consentModal!.setAttribute("aria-hidden", "false");

		if (!inline) {
			// Focus management
			this.focusTrap?.deactivate();
			this.focusTrap = new FocusTrap(this.consentModal!);
			this.focusTrap.activate();

			// Block scroll if consent required
			if (this.config.consentRequired) {
				document.body.classList.add("dcbl-cc-scroll-locked");
			}
		}
	}

	public hide(): void {
		this.consentModal?.classList.remove("dcbl-cc-modal--visible");
		this.consentModal?.setAttribute("aria-hidden", "true");
		this.settingsModal?.classList.remove("dcbl-cc-modal--visible");
		this.settingsModal?.setAttribute("aria-hidden", "true");
		this.hideOverlay();

		this.focusTrap?.deactivate();
		document.body.classList.remove("dcbl-cc-scroll-locked");
	}

	private hideSettingsOnly(): void {
		this.settingsModal?.classList.remove("dcbl-cc-modal--visible");
		this.settingsModal?.setAttribute("aria-hidden", "true");
	}

	/**
	 * Switch from settings modal back to consent modal.
	 */
	private showConsentFromSettings(): void {
		const layout = this.config.appearance.consentLayout;
		const inline = this.isInlineLayout(layout);

		if (!inline) {
			// Deactivate settings focus trap without restoring focus
			this.focusTrap?.deactivate(false);
		}

		// Hide overlay if consent modal doesn't need it
		if (!this.config.consentRequired && !this.needsOverlay(layout)) {
			this.hideOverlay();
		}

		// Show consent modal
		if (!this.consentModal) {
			this.createConsentModal();
		}

		// Set small initial opacity so element is focusable immediately
		this.consentModal!.style.opacity = "0.01";
		this.consentModal!.classList.add("dcbl-cc-modal--visible");
		this.consentModal!.setAttribute("aria-hidden", "false");

		// Use rAF to ensure styles are applied, then hide settings and focus
		requestAnimationFrame(() => {
			// Remove inline opacity to let CSS transition take over
			this.consentModal!.style.opacity = "";

			this.hideSettingsOnly();

			if (!inline) {
				// Set up focus trap for consent modal
				this.focusTrap = new FocusTrap(this.consentModal!);
				// Focus the "manage preferences" button since user is returning from settings
				const settingsBtn = this.consentModal!.querySelector<HTMLElement>(
					'[data-action="show-settings"]',
				);
				this.focusTrap.activate(settingsBtn ?? undefined);
			}
		});
	}

	public showSettings(): void {
		const wasJustCreated = !this.settingsModal;
		if (!this.settingsModal) {
			this.createSettingsModal();
		}

		// Hide consent modal if visible
		this.consentModal?.classList.remove("dcbl-cc-modal--visible");
		this.consentModal?.setAttribute("aria-hidden", "true");

		const prefsLayout = this.config.appearance.preferencesLayout;
		const inline = this.isInlineLayout(prefsLayout);

		// Preferences modal always uses overlay (it's always a drawer or centered modal)
		if (!inline) {
			this.showOverlay();
		}

		// If just created, force reflow before adding visible class to trigger enter animation
		if (wasJustCreated) {
			void this.settingsModal!.offsetHeight;
		}

		this.settingsModal!.classList.add("dcbl-cc-modal--visible");
		this.settingsModal!.setAttribute("aria-hidden", "false");

		if (!inline) {
			// Focus management
			this.focusTrap?.deactivate(false);
			this.focusTrap = new FocusTrap(this.settingsModal!, () => {
				// Handle Escape key - same logic as close button
				const currentConsent = this.manager.getCurrentConsent();
				if (!currentConsent && this.config.autoShow) {
					this.showConsentFromSettings();
				} else {
					this.manager.hideModal();
				}
			});
			this.focusTrap.activate();
		}
	}

	private showOverlay(): void {
		if (!this.overlay) {
			this.overlay = document.createElement("div");
			this.overlay.className = "dcbl-cc-overlay";
			this.overlay.setAttribute("aria-hidden", "true");
			document.body.appendChild(this.overlay);

			// Prevent clicks on overlay from stealing focus from the modal
			this.overlay.addEventListener("mousedown", (e) => {
				e.preventDefault();
				// Refocus the active modal
				const activeModal = this.settingsModal?.classList.contains("dcbl-cc-modal--visible")
					? this.settingsModal
					: this.consentModal;
				activeModal?.focus();
			});
		}

		this.overlay.classList.add("dcbl-cc-overlay--visible");
	}

	private hideOverlay(): void {
		this.overlay?.classList.remove("dcbl-cc-overlay--visible");
	}

	private t(key: string): string {
		return this.manager.getTranslation(key);
	}

	private escapeHtml(str: string): string {
		const div = document.createElement("div");
		div.textContent = str;
		return div.innerHTML;
	}

	private createConsentModal(): void {
		const layout = this.config.appearance.consentLayout;
		const inline = this.isInlineLayout(layout);
		const modal = document.createElement("div");
		modal.id = "dcbl-cc-consent-modal";
		modal.className = `dcbl-cc-modal dcbl-cc-modal--consent dcbl-cc-modal--${layout}`;
		modal.setAttribute("role", "dialog");
		if (!inline) {
			modal.setAttribute("aria-modal", "true");
		}
		modal.setAttribute("aria-labelledby", "dcbl-cc-title");
		modal.setAttribute("aria-describedby", "dcbl-cc-description");
		modal.setAttribute("aria-hidden", "true");
		modal.tabIndex = -1;

		modal.innerHTML = `
      <div class="dcbl-cc-modal__container">
        <div class="dcbl-cc-modal__content">
          <h2 id="dcbl-cc-title" class="dcbl-cc-modal__title">${this.escapeHtml(this.t("consent_modal_title"))}</h2>
          <div id="dcbl-cc-description" class="dcbl-cc-modal__description">${this.t("consent_modal_description")}</div>
        </div>
        <div class="dcbl-cc-modal__actions">
          <button type="button" class="dcbl-cc-btn dcbl-cc-btn--primary" data-action="accept-all">
            ${this.escapeHtml(this.t("accept_all_btn"))}
          </button>
          <button type="button" class="dcbl-cc-btn dcbl-cc-btn--secondary" data-action="reject-all">
            ${this.escapeHtml(this.t("reject_all_btn"))}
          </button>
          <button type="button" class="dcbl-cc-btn dcbl-cc-btn--tertiary" data-action="show-settings">
            ${this.escapeHtml(this.t("settings_btn"))}
          </button>
        </div>
      </div>
    `;

		// Bind events
		modal
			.querySelector('[data-action="accept-all"]')
			?.addEventListener("click", () => {
				this.manager.acceptAll();
			});

		modal
			.querySelector('[data-action="reject-all"]')
			?.addEventListener("click", () => {
				this.manager.rejectAll();
			});

		modal
			.querySelector('[data-action="show-settings"]')
			?.addEventListener("click", () => {
				this.showSettings();
			});

		if (inline) {
			document.body.prepend(modal);
		} else {
			document.body.appendChild(modal);
		}
		this.consentModal = modal;
	}

	private createSettingsModal(): void {
		const prefsLayout = this.config.appearance.preferencesLayout;
		const inline = this.isInlineLayout(prefsLayout);
		const modal = document.createElement("div");
		modal.id = "dcbl-cc-settings-modal";
		modal.className = `dcbl-cc-modal dcbl-cc-modal--settings dcbl-cc-modal--prefs-${prefsLayout}`;
		modal.setAttribute("role", "dialog");
		if (!inline) {
			modal.setAttribute("aria-modal", "true");
		}
		modal.setAttribute("aria-labelledby", "dcbl-cc-settings-title");
		modal.setAttribute("aria-hidden", "true");
		modal.tabIndex = -1;

		const categoriesHtml = this.buildCategoriesHtml();

		modal.innerHTML = `
      <div class="dcbl-cc-modal__container">
        <div class="dcbl-cc-modal__header">
          <h2 id="dcbl-cc-settings-title" class="dcbl-cc-modal__title">${this.escapeHtml(this.t("settings_btn"))}</h2>
          <button type="button" class="dcbl-cc-modal__close" data-action="close" aria-label="${this.escapeHtml(this.t("close_btn"))}">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
          </button>
        </div>
        <div class="dcbl-cc-modal__body">
          <div class="dcbl-cc-categories">
            ${categoriesHtml}
          </div>
        </div>
        <div class="dcbl-cc-modal__footer">
          <button type="button" class="dcbl-cc-btn dcbl-cc-btn--secondary" data-action="accept-all">
            ${this.escapeHtml(this.t("accept_all_btn"))}
          </button>
          <button type="button" class="dcbl-cc-btn dcbl-cc-btn--secondary" data-action="reject-all">
            ${this.escapeHtml(this.t("reject_all_btn"))}
          </button>
          <button type="button" class="dcbl-cc-btn dcbl-cc-btn--primary dcbl-cc-btn--right" data-action="save">
            ${this.escapeHtml(this.t("save_btn"))}
          </button>
        </div>
      </div>
    `;

		// Bind events
		modal
			.querySelector('[data-action="close"]')
			?.addEventListener("click", () => {
				// Show consent modal again if no consent given, otherwise hide via manager
				const currentConsent = this.manager.getCurrentConsent();
				if (!currentConsent && this.config.autoShow) {
					this.showConsentFromSettings();
				} else {
					this.manager.hideModal();
				}
			});

		modal
			.querySelector('[data-action="save"]')
			?.addEventListener("click", () => {
				const { categories, services } = this.getSelectedPreferences(modal);
				this.manager.savePreferences(categories, services);
			});

		modal
			.querySelector('[data-action="accept-all"]')
			?.addEventListener("click", () => {
				this.manager.acceptAll();
			});

		modal
			.querySelector('[data-action="reject-all"]')
			?.addEventListener("click", () => {
				this.manager.rejectAll();
			});

		if (inline) {
			document.body.prepend(modal);
		} else {
			document.body.appendChild(modal);
		}
		this.settingsModal = modal;

		// Bind accordion and toggle sync behavior
		this.bindAccordionEvents(modal);
		this.bindToggleSyncEvents(modal);
	}

	private buildCategoriesHtml(): string {
		const currentConsent = this.manager.getCurrentConsent();
		const currentServices = this.manager.getCurrentServices();
		const cookiesByCategory = this.manager.getCookiesByCategory();
		const providersByCategory = this.manager.getProvidersByCategory();
		const showProviders = this.config.appearance.showProviderSelection;
		const showCookieTable = this.config.appearance.showCookieTable;
		let html = "";

		for (const [key, storage] of Object.entries(this.config.storages)) {
			if (!storage?.enabled) continue;

			const titleKey = `${key}_title`;
			const descKey = `${key}_description`;
			const isChecked = currentConsent
				? (currentConsent[key] ?? storage.default)
				: storage.default;
			const isDisabled = storage.readonly;
			const providers = providersByCategory.get(key) ?? [];
			const cookies = cookiesByCategory.get(key) ?? [];

			// Determine accepted services for this category
			const acceptedServices =
				currentServices?.[key] ??
				(isChecked ? providers.map((p) => p.code) : []);

			// Check for indeterminate state (some but not all providers selected)
			const allSelected =
				providers.length > 0 && acceptedServices.length === providers.length;
			const noneSelected = acceptedServices.length === 0;
			const isIndeterminate =
				!allSelected && !noneSelected && providers.length > 1;

			// Provider count hint
			const serviceCountHtml =
				providers.length > 0
					? `<span class="dcbl-cc-category__service-count">${providers.length} ${this.escapeHtml(this.t("services_label"))}</span>`
					: "";

			// Provider list
			const providerListHtml =
				showProviders && providers.length > 0
					? this.buildProviderListHtml(
							key,
							providers,
							acceptedServices,
							isDisabled,
						)
					: "";

			// Cookie table
			const cookieTableHtml =
				showCookieTable && cookies.length > 0
					? this.buildCookieTableHtml(cookies)
					: "";

			// Determine if accordion body has content
			const hasBody =
				providerListHtml || cookieTableHtml || this.t(descKey) !== descKey;

			// Build aria-controls list of provider checkbox IDs
			const providerIds = providers.map((p) => `dcbl-cc-svc-${key}-${p.code}`);
			const ariaControls =
				providerIds.length > 0
					? ` aria-controls="${providerIds.map((id) => this.escapeHtml(id)).join(" ")}"`
					: "";

			html += `
        <div class="dcbl-cc-category dcbl-cc-accordion" data-category="${this.escapeHtml(key)}">
          <div class="dcbl-cc-accordion__header"${hasBody ? ' role="button" tabindex="0" aria-expanded="false"' : ""}>
            ${
							hasBody
								? `<svg class="dcbl-cc-accordion__chevron" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor"/>
              </svg>`
								: ""
						}
            <div class="dcbl-cc-category__info">
              <h3 class="dcbl-cc-category__title" id="dcbl-cc-cat-${this.escapeHtml(key)}">${this.escapeHtml(this.t(titleKey))}</h3>
              ${isDisabled ? `<span class="dcbl-cc-category__badge">${this.escapeHtml(this.t("always_enabled"))}</span>` : ""}
              ${serviceCountHtml}
            </div>
            <label class="dcbl-cc-toggle" onclick="event.stopPropagation()">
              <input
                type="checkbox"
                name="storage_${key}"
                data-storage="${key}"
                ${isChecked ? "checked" : ""}
                ${isDisabled ? "disabled" : ""}
                ${isIndeterminate ? 'data-indeterminate="true"' : ""}
                aria-label="${this.escapeHtml(this.t(titleKey))}"${ariaControls}
              />
              <span class="dcbl-cc-toggle__slider"></span>
            </label>
          </div>
		  <p class="dcbl-cc-category__description">${this.t(descKey)}</p>
          ${
						hasBody
							? `<div class="dcbl-cc-accordion__body" aria-hidden="true">
              ${providerListHtml}
              ${cookieTableHtml}
            </div>`
							: ""
					}
        </div>
      `;
		}

		return html;
	}

	private buildProviderListHtml(
		categoryCode: string,
		providers: import("./config").ServiceInfo[],
		acceptedServices: string[],
		categoryDisabled: boolean,
	): string {
		let html = `<div class="dcbl-cc-providers" role="group" aria-labelledby="dcbl-cc-cat-${this.escapeHtml(categoryCode)}">`;

		for (const provider of providers) {
			const isChecked = acceptedServices.includes(provider.code);
			const checkboxId = `dcbl-cc-svc-${categoryCode}-${provider.code}`;
			const providerLabel = provider.link
				? `<a href="${this.escapeHtml(provider.link)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(provider.name)}</a>`
				: this.escapeHtml(provider.name);

			html += `
        <div class="dcbl-cc-provider">
          <span class="dcbl-cc-provider__name" id="${this.escapeHtml(checkboxId)}-label">${providerLabel}</span>
          <label class="dcbl-cc-toggle dcbl-cc-toggle--small" onclick="event.stopPropagation()">
            <input
              type="checkbox"
              id="${this.escapeHtml(checkboxId)}"
              data-category="${this.escapeHtml(categoryCode)}"
              data-service="${this.escapeHtml(provider.code)}"
              ${isChecked ? "checked" : ""}
              ${categoryDisabled ? "disabled" : ""}
              aria-labelledby="${this.escapeHtml(checkboxId)}-label"
            />
            <span class="dcbl-cc-toggle__slider"></span>
          </label>
        </div>
      `;
		}

		html += "</div>";
		return html;
	}

	private buildCookieTableHtml(
		cookies: Array<{
			name: string;
			purpose: string;
			processingTime: string;
			cookieProvider: { name: string; link: string };
		}>,
	): string {
		let items = "";
		for (const cookie of cookies) {
			const providerHtml = cookie.cookieProvider.link
				? `<a href="${this.escapeHtml(cookie.cookieProvider.link)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(cookie.cookieProvider.name)}</a>`
				: this.escapeHtml(cookie.cookieProvider.name);

			items += `
        <li class="dcbl-cc-cookie-list-item">
          <div><code>${this.escapeHtml(cookie.name)}</code></div>
          <div>${this.escapeHtml(cookie.purpose)}</div>
		  <div class="dcbl-cc-cookie-list-item__details">
		    <strong>${this.escapeHtml(this.t("cookie_table_provider"))}</strong>
		    ${providerHtml}
		  </div>
          <div class="dcbl-cc-cookie-list-item__details">
		  	<strong>${this.escapeHtml(this.t("cookie_table_duration"))}</strong>
			${this.escapeHtml(cookie.processingTime)}
		  </div>
        </li>
      `;
		}

		return `
      <div class="dcbl-cc-cookie-list-wrap">
		<!-- title could go here if needed -->
        <ul class="dcbl-cc-cookie-list">
          ${items}
        </ul>
      </div>
    `;
	}

	private getSelectedPreferences(modal: HTMLElement): {
		categories: Record<string, boolean>;
		services: Record<string, string[]>;
	} {
		const categories: Record<string, boolean> = {};
		const services: Record<string, string[]> = {};

		// Read category-level checkboxes
		modal
			.querySelectorAll<HTMLInputElement>("input[data-storage]")
			.forEach((checkbox) => {
				const storage = checkbox.dataset.storage;
				if (storage) {
					categories[storage] = checkbox.checked;
					services[storage] = [];
				}
			});

		// Read service-level checkboxes
		modal
			.querySelectorAll<HTMLInputElement>("input[data-service]")
			.forEach((checkbox) => {
				const catCode = checkbox.dataset.category;
				const serviceCode = checkbox.dataset.service;
				if (catCode && serviceCode && checkbox.checked) {
					if (!services[catCode]) services[catCode] = [];
					services[catCode].push(serviceCode);
				}
			});

		return { categories, services };
	}

	private bindAccordionEvents(modal: HTMLElement): void {
		// Set initial indeterminate states (can't be set via HTML attribute)
		modal
			.querySelectorAll<HTMLInputElement>('[data-indeterminate="true"]')
			.forEach((input) => {
				input.indeterminate = true;
			});

		// Accordion expand/collapse
		modal
			.querySelectorAll<HTMLElement>(
				'.dcbl-cc-accordion__header[role="button"]',
			)
			.forEach((header) => {
				const toggle = () => {
					const accordion = header.closest(".dcbl-cc-accordion")!;
					const isExpanded = accordion.classList.contains(
						"dcbl-cc-accordion--open",
					);
					accordion.classList.toggle("dcbl-cc-accordion--open");
					header.setAttribute("aria-expanded", String(!isExpanded));
					accordion
						.querySelector(".dcbl-cc-accordion__body")!
						.setAttribute("aria-hidden", String(isExpanded));
				};

				header.addEventListener("click", toggle);
				header.addEventListener("keydown", (e: KeyboardEvent) => {
					if (e.key === "Enter" || e.key === " ") {
						// Don't intercept when focus is on a toggle input
						if ((e.target as HTMLElement).closest(".dcbl-cc-toggle")) {
							return;
						}
						e.preventDefault();
						toggle();
					}
				});
			});
	}

	private bindToggleSyncEvents(modal: HTMLElement): void {
		// Allow Enter key to toggle checkboxes (native only supports Space)
		modal
			.querySelectorAll<HTMLInputElement>(".dcbl-cc-toggle input")
			.forEach((input) => {
				input.addEventListener("keydown", (e: KeyboardEvent) => {
					if (e.key === "Enter") {
						e.preventDefault();
						input.checked = !input.checked;
						input.dispatchEvent(new Event("change", { bubbles: true }));
					}
				});
			});

		// Category toggle → sync all service toggles in that category
		modal
			.querySelectorAll<HTMLInputElement>("input[data-storage]")
			.forEach((categoryInput) => {
				categoryInput.addEventListener("change", () => {
					const catCode = categoryInput.dataset.storage!;
					modal
						.querySelectorAll<HTMLInputElement>(
							`input[data-category="${catCode}"][data-service]`,
						)
						.forEach((si) => {
							si.checked = categoryInput.checked;
						});
					categoryInput.indeterminate = false;
				});
			});

		// Service toggle → sync category toggle
		modal
			.querySelectorAll<HTMLInputElement>("input[data-service]")
			.forEach((serviceInput) => {
				serviceInput.addEventListener("change", () => {
					const catCode = serviceInput.dataset.category!;
					this.syncCategoryToggle(modal, catCode);
				});
			});
	}

	private syncCategoryToggle(modal: HTMLElement, categoryCode: string): void {
		const categoryInput = modal.querySelector<HTMLInputElement>(
			`input[data-storage="${categoryCode}"]`,
		);
		if (!categoryInput || categoryInput.disabled) return;

		const serviceInputs = modal.querySelectorAll<HTMLInputElement>(
			`input[data-category="${categoryCode}"][data-service]`,
		);
		if (serviceInputs.length === 0) return;

		const checkedCount = Array.from(serviceInputs).filter(
			(i) => i.checked,
		).length;
		const allChecked = checkedCount === serviceInputs.length;
		const noneChecked = checkedCount === 0;

		categoryInput.checked = checkedCount > 0;
		categoryInput.indeterminate = !allChecked && !noneChecked;
	}
}

/**
 * Simple focus trap for modal accessibility
 */
class FocusTrap {
	private element: HTMLElement;
	private previousActiveElement: Element | null = null;
	private boundHandleKeyDown: (event: KeyboardEvent) => void;
	private onEscape?: () => void;

	constructor(element: HTMLElement, onEscape?: () => void) {
		this.element = element;
		this.onEscape = onEscape;
		this.boundHandleKeyDown = this.handleKeyDown.bind(this);
	}

	public activate(initialFocusElement?: HTMLElement): void {
		this.previousActiveElement = document.activeElement;

		if (initialFocusElement) {
			// Focus specific element (e.g., when returning from settings)
			initialFocusElement.focus();
		} else {
			// Focus the modal container itself (requires tabindex="-1")
			// This announces the dialog without accidentally activating a button
			this.element.focus();
		}

		document.addEventListener("keydown", this.boundHandleKeyDown);
	}

	public deactivate(restoreFocus = true): void {
		document.removeEventListener("keydown", this.boundHandleKeyDown);

		if (restoreFocus && this.previousActiveElement instanceof HTMLElement) {
			this.previousActiveElement.focus();
		}
	}

	private handleKeyDown(event: KeyboardEvent): void {
		if (event.key === "Escape" && this.onEscape) {
			event.preventDefault();
			this.onEscape();
			return;
		}

		if (event.key !== "Tab") return;

		const focusable = this.getFocusableElements();
		if (focusable.length === 0) return;

		const first = focusable[0] as HTMLElement;
		const last = focusable[focusable.length - 1] as HTMLElement;

		if (event.shiftKey && document.activeElement === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}

	private getFocusableElements(): NodeListOf<Element> {
		return this.element.querySelectorAll(
			'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
		);
	}
}
